import React, { useEffect, useRef } from 'react';
import './Checkbox.css';

/**
 * Checkbox — design-system primitive (plan 96, REQ-20).
 *
 * Introduced by the plan-96 UI mock. Presentational, fully controlled:
 * state lives in the parent; this component only renders `checked` /
 * `indeterminate` / `disabled` and forwards `onChange`.
 *
 * - Token-based styling (no hex literals).
 * - Visible focus ring (`:focus-visible` on the hidden native input).
 * - `indeterminate` support (select-all headers with partial selection).
 * - Keyboard-operable (native input semantics preserved).
 * - >=44px touch target on coarse pointers / mobile via expanded hit area.
 */
function Checkbox({
  checked = false,
  indeterminate = false,
  disabled = false,
  onChange,
  label = null,
  size = 'md',
  className = '',
  'aria-label': ariaLabel,
  ...rest
}) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate && !checked;
    }
  }, [indeterminate, checked]);

  const classes = [
    'ui-checkbox',
    `ui-checkbox--${size}`,
    checked ? 'ui-checkbox--checked' : '',
    !checked && indeterminate ? 'ui-checkbox--indeterminate' : '',
    disabled ? 'ui-checkbox--disabled' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <label className={classes}>
      <input
        ref={inputRef}
        type="checkbox"
        className="ui-checkbox__input"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-checked={!checked && indeterminate ? 'mixed' : undefined}
        onChange={(e) => { if (onChange) onChange(e); }}
        {...rest}
      />
      <span className="ui-checkbox__box" aria-hidden="true">
        {checked && (
          <svg className="ui-checkbox__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        {!checked && indeterminate && (
          <svg className="ui-checkbox__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round">
            <line x1="6" y1="12" x2="18" y2="12" />
          </svg>
        )}
      </span>
      {label && <span className="ui-checkbox__label">{label}</span>}
    </label>
  );
}

export default Checkbox;
